import { Box, Button, Flex, Text, Tooltip } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  PerimeterAllIcon,
  PerimeterMeetingCircleIcon,
  PerimeterMyCirclesIcon,
  PerimeterMyRolesIcon,
} from 'src/icons'
import { CirclePerimeter } from '../circlePerimeter'

interface Props {
  offered: CirclePerimeter[]
  value: CirclePerimeter
  // Name of the meeting's circle, shown by the "meeting circle" filter.
  meetingCircleName?: string
  onChange(perimeter: CirclePerimeter): void
}

const perimeterIcons: Record<CirclePerimeter, React.ElementType> = {
  meetingCircle: PerimeterMeetingCircleIcon,
  myRoles: PerimeterMyRolesIcon,
  myCircles: PerimeterMyCirclesIcon,
  all: PerimeterAllIcon,
}

// Attribute marking the bar, so the search field knows focus moved INTO the
// list (Tab) rather than out of it — see useKeepOpenOnBarFocus.
export const perimeterBarAttribute = 'data-perimeter-bar'

// #123: the filters of the circle picker, at the top of its list. Before, a
// small button left of the field cycled through them, and people did not see
// it was a filter. Each filter is an icon with a tooltip; the active one also
// shows its name.
export default function CirclePerimeterBar({
  offered,
  value,
  meetingCircleName,
  onChange,
}: Props) {
  const { t } = useTranslation()

  const labelOf = (perimeter: CirclePerimeter) =>
    t(`CirclePerimeter.${perimeter}`, { circle: meetingCircleName })

  return (
    <Box
      {...{ [perimeterBarAttribute]: '' }}
      px={2}
      pt={2}
      pb={2}
      borderBottom="1px solid"
      borderColor="inherit"
      // The list must stay open and the field focused: a mousedown here would
      // otherwise blur the field and select the highlighted result.
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Flex align="center" gap={1} wrap="wrap">
        <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }}>
          {t('CirclePerimeter.filter')}
        </Text>
        {offered.map((perimeter) => {
          const Icon = perimeterIcons[perimeter]
          const isActive = perimeter === value
          const label = labelOf(perimeter)
          return (
            <Tooltip
              key={perimeter}
              label={`${label} — ${t(`CirclePerimeter.${perimeter}Help`)}`}
              placement="top"
              hasArrow
              openDelay={300}
            >
              <Button
                size="xs"
                borderRadius="full"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'blue' : undefined}
                fontWeight="normal"
                leftIcon={isActive ? <Icon size={14} /> : undefined}
                aria-label={label}
                aria-pressed={isActive}
                onClick={() => onChange(perimeter)}
              >
                {isActive ? label : <Icon size={14} />}
              </Button>
            </Tooltip>
          )
        })}
      </Flex>
    </Box>
  )
}
